// Sac et marchand : grille des objets (rareté en couleur de bordure), feuille de détail (utiliser, équiper,
// donner, vendre, jeter après confirmation), poids porté et or ; onglet Marchand quand le lieu en a un.
import { useEffect, useRef, useState } from "preact/hooks";
import { createPortal } from "preact/compat";
import type {
  BonusKey, CharacterView, ClientView, ConditionId, EquipSlot, ItemDef, ItemKind, ItemView, Rarity,
} from "../../shared/types";
import { send } from "../store";
import "./Inventory.css";

export interface InventoryProps { view: ClientView }

type Onglet = "sac" | "marchand";
type Marchand = NonNullable<ClientView["merchant"]>;
type Personne = ClientView["othersHere"][number];

// Libellés du jeu, en français : les définitions d'objets arrivent déjà dans la vue.
const RARETE_LIBELLE: Record<Rarity, string> = {
  commun: "Commun", peu_commun: "Peu commun", rare: "Rare", epique: "Épique", legendaire: "Légendaire",
};
const RARETE_RANG: Record<Rarity, number> = { commun: 0, peu_commun: 1, rare: 2, epique: 3, legendaire: 4 };
const GENRE_LIBELLE: Record<ItemKind, string> = {
  arme: "Arme", armure: "Armure", bouclier: "Bouclier", consommable: "Consommable", nourriture: "Nourriture",
  materiau: "Matériau", quete: "Objet de quête", tresor: "Trésor", outil: "Outil",
};
const EMPLACEMENT_LIBELLE: Record<EquipSlot, string> = {
  main: "Main", secondaire: "Secondaire", armure: "Armure", accessoire: "Accessoire",
};
const BONUS_LIBELLE: Record<BonusKey, string> = {
  FOR: "Force", DEX: "Dextérité", CON: "Constitution", INT: "Intelligence", SAG: "Sagesse", CHA: "Charisme",
  attaque: "Attaque", degats: "Dégâts", ca: "Classe d’armure", pvmax: "PV max", manamax: "Mana max",
};
const ETAT_LIBELLE: Record<ConditionId, string> = {
  empoisonne: "empoisonnement", saignement: "saignement", etourdi: "étourdissement", beni: "béni",
  affame: "affamé", famine: "famine", epuise: "épuisé", invisible: "invisible", protege: "protégé",
  enflamme: "enflammé", gele: "gelé", maudit: "maudit", inspire: "inspiré", cicatrice_oubli: "cicatrice d’oubli",
  repose: "reposé",
};
/** Noms qui se boivent plutôt que de se manger ou de s'utiliser. */
const BREUVAGE = /potion|elixir|hydromel|breuvage|philtre|antidote|outre|\bvin\b|biere|\beau\b|lait|infusion|tisane|cidre|\bjus\b/;

const fmt = (n: number, decimales = 0): string => n.toLocaleString("fr-FR", { maximumFractionDigits: decimales });
const kg = (n: number): string => `${fmt(n, 2)} kg`;
const signe = (n: number): string => (n < 0 ? `−${fmt(-n)}` : `+${fmt(n)}`);
const sansAccents = (s: string): string => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Verbe du bouton d'usage (Boire, Manger, Utiliser), ou null si l'objet ne s'utilise pas. */
function verbeUsage(def: ItemDef): string | null {
  if (!def.use && def.kind !== "consommable" && def.kind !== "nourriture") return null;
  if (BREUVAGE.test(sansAccents(`${def.key} ${def.name}`))) return "Boire";
  return def.kind === "nourriture" ? "Manger" : "Utiliser";
}

interface Ligne { cle: string; valeur: string }

/** Lignes de la fiche : dégâts, défense, bonus et effet d'usage. */
function lignesStats(def: ItemDef): Ligne[] {
  const lignes: Ligne[] = [];
  if (def.damage) lignes.push({ cle: "Dégâts", valeur: `${def.damage} + ${def.damageAttr ?? "FOR"}` });
  if (def.twoHanded) lignes.push({ cle: "Maniement", valeur: "À deux mains" });
  if (def.armor) lignes.push({ cle: "Classe d’armure", valeur: signe(def.armor) });
  for (const cle of Object.keys(def.bonuses ?? {}) as BonusKey[]) {
    const valeur = def.bonuses?.[cle];
    if (valeur) lignes.push({ cle: BONUS_LIBELLE[cle] ?? cle, valeur: signe(valeur) });
  }
  const u = def.use;
  if (u?.heal) lignes.push({ cle: "Soin", valeur: `${u.heal} PV` });
  if (u?.mana) lignes.push({ cle: "Mana", valeur: signe(u.mana) });
  if (u?.satiety) lignes.push({ cle: "Satiété", valeur: signe(u.satiety) });
  if (u?.energy) lignes.push({ cle: "Énergie", valeur: signe(u.energy) });
  if (u?.cure?.length) lignes.push({ cle: "Guérit", valeur: u.cure.map((id) => ETAT_LIBELLE[id] ?? id).join(", ") });
  if (u?.addCondition) {
    const { id, hours } = u.addCondition;
    lignes.push({ cle: "Effet", valeur: `${ETAT_LIBELLE[id] ?? id} pendant ${fmt(hours, 1)} h` });
  }
  return lignes;
}

/** Objets les plus précieux d'abord, puis ordre alphabétique. */
function parRaretePuisNom(a: ItemView, b: ItemView): number {
  return RARETE_RANG[b.def.rarity] - RARETE_RANG[a.def.rarity] || a.def.name.localeCompare(b.def.name, "fr");
}

export function Inventory(props: InventoryProps) {
  const { view } = props;
  const [onglet, setOnglet] = useState<Onglet>("sac");
  const [iidOuvert, setIidOuvert] = useState<string | null>(null);

  const c = view.character;
  const marchand = view.merchant;
  // Sans marchand sur place, on reste sur le sac même si l'onglet Marchand était choisi.
  const actif: Onglet = onglet === "marchand" && marchand ? "marchand" : "sac";
  const ouvert = c && iidOuvert ? c.inventory.find((i) => i.iid === iidOuvert) : undefined;

  if (!c) {
    return (
      <section class="inventory" aria-label="Sac">
        <div class="inventory-vide">
          <span class="inventory-vide__icone" aria-hidden="true">🎒</span>
          <p class="inventory-vide__titre">Pas encore de héros</p>
          <p class="inventory-vide__texte">Ton sac t’attendra dès que ton personnage aura pris la route.</p>
        </div>
      </section>
    );
  }

  return (
    <section class="inventory" aria-label="Sac et marchand">
      {marchand && (
        <div class="inventory-onglets" role="tablist" aria-label="Sac ou marchand">
          <button
            type="button"
            role="tab"
            aria-selected={actif === "sac"}
            class={actif === "sac" ? "inventory-onglet inventory-onglet--actif" : "inventory-onglet"}
            onClick={() => setOnglet("sac")}
          >
            🎒 Sac
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={actif === "marchand"}
            class={actif === "marchand" ? "inventory-onglet inventory-onglet--actif" : "inventory-onglet"}
            onClick={() => setOnglet("marchand")}
          >
            🛒 Marchand
          </button>
        </div>
      )}

      {actif === "marchand" && marchand ? (
        <PanneauMarche c={c} marchand={marchand} />
      ) : (
        <PanneauSac c={c} onOpen={(iid) => setIidOuvert(iid)} />
      )}

      {ouvert && (
        <FeuilleObjet
          key={ouvert.iid}
          item={ouvert}
          personnes={view.othersHere}
          tauxRachat={marchand ? marchand.sellRate : null}
          onClose={() => setIidOuvert(null)}
        />
      )}
    </section>
  );
}

function PanneauSac({ c, onOpen }: { c: CharacterView; onOpen: (iid: string) => void }) {
  const equipes = c.inventory.filter((i) => i.equipped);
  const sac = c.inventory.filter((i) => !i.equipped).sort(parRaretePuisNom);
  const enEmplacement = (slot: EquipSlot): ItemView[] => equipes.filter((i) => i.equipped === slot);

  const main: ItemView | undefined = enEmplacement("main")[0];
  // Une arme à deux mains occupe aussi la main secondaire.
  const secondaire: ItemView | undefined = enEmplacement("secondaire").find((i) => i.iid !== main?.iid);
  const secondaireBloquee = main?.def.twoHanded === true;
  const armure: ItemView | undefined = enEmplacement("armure")[0];
  const accessoires = enEmplacement("accessoire");
  const nbAccessoires = Math.max(2, accessoires.length);

  return (
    <>
      <ResumeSac c={c} />

      <section class="inventory-section" aria-labelledby="inventory-equipe-titre">
        <h2 id="inventory-equipe-titre" class="inventory-section__titre">Équipé</h2>
        <ul class="inventory-equipe">
          <li><Emplacement libelle="Main" item={main} onOpen={onOpen} /></li>
          <li><Emplacement libelle="Secondaire" item={secondaire} bloquee={secondaireBloquee} onOpen={onOpen} /></li>
          <li><Emplacement libelle="Armure" item={armure} onOpen={onOpen} /></li>
          {Array.from({ length: nbAccessoires }, (_, k) => (
            <li key={`accessoire-${k}`}>
              <Emplacement libelle="Accessoire" item={accessoires[k]} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      </section>

      <section class="inventory-section" aria-labelledby="inventory-sac-titre">
        <h2 id="inventory-sac-titre" class="inventory-section__titre">Dans le sac</h2>
        {sac.length > 0 ? (
          <ul class="inventory-grille">
            {sac.map((item) => (
              <li key={item.iid}>
                <CaseObjet item={item} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        ) : equipes.length > 0 ? (
          <p class="inventory-note">Rien d’autre à porter pour l’instant.</p>
        ) : (
          <div class="inventory-vide">
            <span class="inventory-vide__icone" aria-hidden="true">🎒</span>
            <p class="inventory-vide__titre">Ton sac est vide</p>
            <p class="inventory-vide__texte">
              Les trouvailles du chemin, les butins de la nuit et les emplettes du marché atterriront ici.
            </p>
          </div>
        )}
      </section>
    </>
  );
}

function ResumeSac({ c }: { c: CharacterView }) {
  const pct = c.capacity > 0 ? Math.min(100, (c.carried / c.capacity) * 100) : 0;
  const trop = c.carried > c.capacity;
  return (
    <div class="inventory-resume card">
      <p class="inventory-or">
        <span aria-hidden="true">🪙</span>
        <strong class="inventory-or__montant">{fmt(c.gold)}</strong>
        <span class="inventory-or__unite">or</span>
      </p>
      <div class="inventory-poids">
        <div class="inventory-poids__ligne">
          <span>Poids porté</span>
          <span class={trop ? "inventory-poids__chiffre inventory-poids__chiffre--trop" : "inventory-poids__chiffre"}>
            {`${kg(c.carried)} / ${kg(c.capacity)}`}
          </span>
        </div>
        <div
          class="inventory-poids__barre"
          role="meter"
          aria-label="Poids porté"
          aria-valuemin={0}
          aria-valuemax={c.capacity}
          aria-valuenow={c.carried}
          aria-valuetext={`${kg(c.carried)} sur ${kg(c.capacity)}`}
        >
          <div
            class={trop ? "inventory-poids__remplissage inventory-poids__remplissage--trop" : "inventory-poids__remplissage"}
            style={{ width: `${pct}%` }}
          />
        </div>
        {trop && <p class="inventory-poids__alerte">Tu croules sous le poids.</p>}
      </div>
    </div>
  );
}

function Emplacement(props: { libelle: string; item?: ItemView; bloquee?: boolean; onOpen: (iid: string) => void }) {
  const { libelle, item, bloquee = false, onOpen } = props;
  if (item) {
    return (
      <button
        type="button"
        class={`inventory-slot inventory-rarity--${item.def.rarity}`}
        onClick={() => onOpen(item.iid)}
        aria-label={`${libelle} : ${item.def.name}`}
      >
        <span class="inventory-slot__libelle" aria-hidden="true">{libelle}</span>
        <span class="inventory-slot__icone" aria-hidden="true">{item.def.icon}</span>
        <span class="inventory-slot__nom">{item.def.name}</span>
        <span class="inventory-slot__fleche" aria-hidden="true">›</span>
      </button>
    );
  }
  return (
    <div class={bloquee ? "inventory-slot inventory-slot--vide inventory-slot--bloque" : "inventory-slot inventory-slot--vide"}>
      <span class="inventory-slot__libelle">{libelle}</span>
      <span class="inventory-slot__nom">{bloquee ? "Occupée par une arme à deux mains" : "Vide"}</span>
    </div>
  );
}

function CaseObjet({ item, onOpen }: { item: ItemView; onOpen: (iid: string) => void }) {
  const { def, qty } = item;
  const legendaire = def.rarity === "legendaire" ? " inventory-case--legendaire" : "";
  const libelle = `${def.name}${qty > 1 ? `, quantité ${qty}` : ""}, ${RARETE_LIBELLE[def.rarity] ?? def.rarity}`;
  return (
    <button
      type="button"
      class={`inventory-case inventory-rarity--${def.rarity}${legendaire}`}
      onClick={() => onOpen(item.iid)}
      aria-label={libelle}
    >
      <span class="inventory-case__icone" aria-hidden="true">{def.icon}</span>
      {qty > 1 && <span class="inventory-case__quantite" aria-hidden="true">{qty}</span>}
    </button>
  );
}

function PanneauMarche({ c, marchand }: { c: CharacterView; marchand: Marchand }) {
  const pourcent = Math.round(marchand.sellRate * 100);
  return (
    <section class="inventory-section" aria-labelledby="inventory-marche-titre">
      <div class="inventory-marche__entete card-parchment">
        <h2 id="inventory-marche-titre" class="inventory-titre">Le marchand</h2>
        <p>{`Il rachète les objets à ${pourcent} % de leur valeur.`}</p>
        <p class="inventory-marche__bourse">{`Ta bourse : 🪙 ${fmt(c.gold)}`}</p>
      </div>

      {marchand.items.length === 0 ? (
        <div class="inventory-vide">
          <span class="inventory-vide__icone" aria-hidden="true">🛒</span>
          <p class="inventory-vide__titre">Étals vides</p>
          <p class="inventory-vide__texte">
            Le marchand n’a rien à montrer pour l’instant. Reviens quand la route aura fait son œuvre.
          </p>
        </div>
      ) : (
        <ul class="inventory-marche__liste">
          {marchand.items.map((article) => {
            const manque = article.price - c.gold;
            const abordable = manque <= 0;
            return (
              <li
                key={article.key}
                class={abordable ? "inventory-marche__ligne" : "inventory-marche__ligne inventory-marche__ligne--cher"}
              >
                <span class="inventory-marche__icone" aria-hidden="true">{article.icon}</span>
                <div class="inventory-marche__texte">
                  <p class="inventory-marche__nom">{article.name}</p>
                  <p class="inventory-marche__desc">{article.description}</p>
                  <p class="inventory-marche__prix">{`🪙 ${fmt(article.price)}`}</p>
                </div>
                <div class="inventory-marche__achat">
                  <button
                    type="button"
                    class="btn btn-primary inventory-action"
                    disabled={!abordable}
                    onClick={() => send({ t: "buy", itemKey: article.key })}
                  >
                    Acheter
                  </button>
                  {!abordable && (
                    <p class="inventory-note">{`Il te manque ${fmt(manque)} 🪙`}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface FeuilleProps {
  item: ItemView;
  personnes: Personne[];
  /** taux de rachat du marchand présent, null s'il n'y en a pas */
  tauxRachat: number | null;
  onClose: () => void;
}

/** Feuille de détail en bas d'écran ; se ferme avec ✕, la zone sombre ou Échap. Rendue hors de la page. */
function FeuilleObjet({ item, personnes, tauxRachat, onClose }: FeuilleProps) {
  const { def, iid, qty } = item;
  const equipe = item.equipped ?? null;
  const verbe = verbeUsage(def);
  // Même règle que le moteur : la vente retire un seul exemplaire, au moins 1 PO ; les objets de quête ne se vendent pas.
  const prixVente = tauxRachat !== null ? Math.max(1, Math.floor(def.value * tauxRachat)) : 0;
  const peutEquiper = !equipe && Boolean(def.slot);
  const peutDonner = !equipe && personnes.length > 0;
  const peutVendre = !equipe && tauxRachat !== null && def.kind !== "quete";
  const peutJeter = !equipe;

  const feuille = useRef<HTMLDivElement>(null);
  const [donner, setDonner] = useState(false);
  const [confirmerJet, setConfirmerJet] = useState(false);

  useEffect(() => {
    feuille.current?.focus();
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, []);

  const stats = lignesStats(def);
  const poids = qty > 1 ? `${kg(def.weight * qty)} (${qty} × ${kg(def.weight)})` : kg(def.weight);

  return createPortal(
    <>
      <div class="inventory-fond" onClick={onClose} aria-hidden="true" />
      <div
        ref={feuille}
        class="inventory-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-feuille-titre"
        tabIndex={-1}
      >
        <div class="inventory-sheet__poignee" aria-hidden="true" />

        <header class="inventory-sheet__entete">
          <span class={`inventory-sheet__medaillon inventory-rarity--${def.rarity}`} aria-hidden="true">{def.icon}</span>
          <div class="inventory-sheet__titres">
            <h2 id="inventory-feuille-titre" class="inventory-sheet__titre">
              {qty > 1 ? `${def.name} ×${qty}` : def.name}
            </h2>
            <p class="inventory-sheet__meta">
              <span class={`inventory-rarete inventory-rarity--${def.rarity}`}>{RARETE_LIBELLE[def.rarity] ?? def.rarity}</span>
              <span>{`${GENRE_LIBELLE[def.kind] ?? def.kind}${def.slot ? ` · ${EMPLACEMENT_LIBELLE[def.slot]}` : ""}`}</span>
            </p>
          </div>
          <button type="button" class="icon-btn" onClick={onClose} aria-label="Fermer">✕</button>
        </header>

        <p class="inventory-sheet__description">{def.description}</p>
        {equipe && <p class="inventory-sheet__equipe">{`Équipé · ${EMPLACEMENT_LIBELLE[equipe]}`}</p>}

        <dl class="inventory-stats card-parchment">
          {stats.map((s, k) => (
            <div class="inventory-stats__ligne" key={k}>
              <dt>{s.cle}</dt>
              <dd>{s.valeur}</dd>
            </div>
          ))}
          <div class="inventory-stats__ligne">
            <dt>Poids</dt>
            <dd>{poids}</dd>
          </div>
          <div class="inventory-stats__ligne">
            <dt>Valeur</dt>
            <dd>{`🪙 ${fmt(def.value)}`}</dd>
          </div>
        </dl>

        <div class="inventory-sheet__actions">
          {verbe && (
            <button type="button" class="btn btn-primary inventory-action" onClick={() => send({ t: "use_item", iid })}>
              {verbe}
            </button>
          )}
          {peutEquiper && (
            <button type="button" class="btn btn-primary inventory-action" onClick={() => send({ t: "equip", iid })}>
              Équiper
            </button>
          )}
          {equipe && (
            <button type="button" class="btn btn-ghost inventory-action" onClick={() => send({ t: "unequip", iid })}>
              Déséquiper
            </button>
          )}

          {peutDonner && !donner && (
            <button type="button" class="btn btn-ghost inventory-action" onClick={() => setDonner(true)}>
              {qty > 1 ? "Donner la pile à…" : "Donner à…"}
            </button>
          )}
          {peutDonner && donner && (
            <div class="inventory-sheet__groupe" role="group" aria-label="Donner à">
              <p class="inventory-note">À qui le donnes-tu ?</p>
              {qty > 1 && <p class="inventory-note">{`Les ${qty} exemplaires partiront ensemble.`}</p>}
              {personnes.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  class="btn btn-ghost inventory-action"
                  onClick={() => {
                    send({ t: "give", iid, toCharacterId: p.id });
                    onClose();
                  }}
                >
                  <span class="inventory-sheet__pastille" style={{ background: p.color }} aria-hidden="true" />
                  <span aria-hidden="true">{p.sigil}</span>
                  {` ${p.name}`}
                </button>
              ))}
              <button type="button" class="btn btn-ghost inventory-action" onClick={() => setDonner(false)}>
                Annuler
              </button>
            </div>
          )}

          {peutVendre && (
            <button
              type="button"
              class="btn btn-ghost inventory-action"
              onClick={() => {
                send({ t: "sell", iid });
                onClose();
              }}
            >
              {qty > 1 ? `Vendre 1 pour ${fmt(prixVente)} PO` : `Vendre pour ${fmt(prixVente)} PO`}
            </button>
          )}

          {peutJeter && !confirmerJet && (
            <button type="button" class="btn btn-danger inventory-action" onClick={() => setConfirmerJet(true)}>
              {qty > 1 ? "Jeter la pile" : "Jeter"}
            </button>
          )}
          {peutJeter && confirmerJet && (
            <div class="inventory-sheet__groupe inventory-sheet__groupe--danger" role="group" aria-label="Confirmer le jet">
              <p>{`Jeter « ${def.name} » pour de bon ?`}</p>
              {qty > 1 && <p class="inventory-note">{`Les ${qty} exemplaires partiront aussi.`}</p>}
              <button
                type="button"
                class="btn btn-danger inventory-action"
                onClick={() => {
                  send({ t: "drop", iid });
                  onClose();
                }}
              >
                Oui, jeter
              </button>
              <button type="button" class="btn btn-ghost inventory-action" onClick={() => setConfirmerJet(false)}>
                Garder
              </button>
            </div>
          )}

          {equipe && <p class="inventory-note">Déséquipe-le pour le donner, le vendre ou le jeter.</p>}
        </div>
      </div>
    </>,
    document.body,
  );
}
